<template>
  <div style="margin-top:20px">

    <div style="width: 100%; background-color: #eee; border: 1px dotted grey; margin-bottom: 25px;">
      <span style="font-size: 26px; font-weight: bold">Fleet Assistant Settings</span><br/>
      <div style="font-style: italic; font-size: 16px; padding-bottom: 10px; margin-right: 10px">
        Configure AI assistant behavior and appearance
      </div>
    </div>

    <div v-if="!loading" style="text-align: left; color: #666;" class="d-flex align-items-center justify-content-center">
      <form @submit.prevent="save_config">
        <table width="90%" style="font-size: 18px; font-weight: bold" cellpadding="10px">
          <!-- Usage -->
          <tr>
            <td colspan="3" style="padding-top: 10px;">
              <span style="color: steelblue; font-weight: bold; font-size: 20px;">Usage</span>
              <hr style="margin: 5px 0 15px 0;"/>
            </td>
          </tr>
          <tr>
            <td align="right" width="400" style="vertical-align: top;">Monthly Usage</td>
            <td width="500">
              <div class="usage-quota-wrap">
                <b-progress :max="100" height="14px" class="usage-progress">
                  <b-progress-bar
                    :value="usagePercent"
                    :variant="usageBarVariant"
                  ></b-progress-bar>
                  <b-progress-bar :value="usageFreePercent" variant="light" class="usage-remaining-bar"></b-progress-bar>
                </b-progress>
                <div class="usage-meta">
                  <span class="usage-quota-remaining">{{ usageRemainingDisplay }}% remaining</span>
                  <span class="usage-period usage-period-right">{{ usagePeriodLabel }}</span>
                </div>
                <a
                  v-if="isAdmin"
                  href="#"
                  class="usage-details-toggle"
                  @click.prevent="usageDetailsExpanded = !usageDetailsExpanded"
                >
                  Usage Details
                  <span :class="{ 'toggle-chevron': true, 'toggle-chevron-open': usageDetailsExpanded }">&#9662;</span>
                </a>
                <div v-if="usageDetailsExpanded && isAdmin" class="usage-details-panel">
                  <div v-if="usageHistoryLoading" class="text-muted small" style="padding: 8px 0;">Loading usage history…</div>
                  <div v-else-if="usageHistory.length === 0" class="text-muted small" style="padding: 8px 0;">No usage history.</div>
                  <table v-else class="usage-history-table">
                    <thead>
                      <tr>
                        <th>Period</th>
                        <th style="text-align: right;">Cost</th>
                        <th style="text-align: right;">Chat Requests</th>
                        <th style="text-align: right;">Doc Uploads</th>
                        <th style="text-align: right;">Tokens</br>(In / Out)</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="row in usageHistory" :key="row.period" :class="{ 'row-current-month': row.is_current }">
                        <td class="history-period">
                          {{ formatHistoryPeriod(row.period) }}
                          <span v-if="row.is_current" class="history-current-badge">CURRENT</span>
                        </td>
                        <td class="history-cost">{{ row.blended_cost_dollars }}</td>
                        <td class="history-count">{{ row.chat_requests.toLocaleString() }}</td>
                        <td class="history-count">{{ row.doc_categorizations.toLocaleString() }}</td>
                        <td class="history-tokens">{{ formatTokenCount(row.input_tokens) }} / {{ formatTokenCount(row.output_tokens) }}</td>
                      </tr>
                    </tbody>
                    <tfoot v-if="usageHistory.length > 1">
                      <tr class="history-total-row">
                        <td style="font-weight: 700;">Total</td>
                        <td class="history-cost" style="font-weight: 700;">{{ usageHistoryTotalCostDollars }}</td>
                        <td class="history-count" style="font-weight: 700;">{{ usageHistoryTotalChatRequests.toLocaleString() }}</td>
                        <td class="history-count" style="font-weight: 700;">{{ usageHistoryTotalDocCategorizations.toLocaleString() }}</td>
                        <td class="history-tokens" style="font-weight: 700;">{{ formatTokenCount(usageHistoryTotalInputTokens) }} / {{ formatTokenCount(usageHistoryTotalOutputTokens) }}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </td>
            <td width="350" style="font-size: 12px; color: #888; font-weight: normal;">
              Need more?
                  <router-link :to="{ name: 'support_ticket_create' }">Open a support ticket</router-link>
                  to request additional capacity.
            </td>
          </tr>
          <tr v-if="canEditCostLimit">
            <td align="right" width="400">Monthly Cost Limit</td>
            <td width="500">
              <b-input-group size="sm">
                <b-input-group-prepend is-text>$</b-input-group-prepend>
                <b-form-input
                  v-model="usageLimitDollars"
                  type="number"
                  min="1"
                  step="1"
                  autocomplete="off"
                ></b-form-input>
              </b-input-group>
            </td>
            <td width="350" style="font-size: 12px; color: #888; font-weight: normal;">
              Tenant-level monthly budget cap for AI usage.
            </td>
          </tr>
          <tr v-if="isAdmin">
            <td align="right" width="400">Reset Usage Stats</td>
            <td width="500">
              <b-button
                size="sm"
                variant="outline-danger"
                @click="reset_usage_stats"
                :disabled="resettingUsage"
              >
                <span v-if="resettingUsage"><b-spinner small></b-spinner> Resetting...</span>
                <span v-else>Reset Current Month Usage</span>
              </b-button>
            </td>
            <td width="350" style="font-size: 12px; color: #888; font-weight: normal;">
              Clears this month&apos;s Fleet Assistant usage counters for the tenant.
            </td>
          </tr>

          <!-- AI Model Settings -->
          <tr>
            <td colspan="3" style="padding-top: 20px;">
              <span style="color: steelblue; font-weight: bold; font-size: 20px;">AI Model</span>
              <hr style="margin: 5px 0 15px 0;"/>
            </td>
          </tr>
          <tr>
            <td align="right" width="400">Chat model</td>
            <td width="500">
              <b-form-select v-model="model.openai_model" :options="openai_model_options"></b-form-select>
            </td>
            <td width="350" style="font-size: 12px; color: #888; font-weight: normal;">Higher models are smarter but cost more. Use GPT-4o+ for overlap detection.</td>
          </tr>
          <tr>
            <td align="right" width="400">Document categorization model</td>
            <td width="500">
              <b-form-select v-model="model.openai_doc_model" :options="openai_doc_model_options"></b-form-select>
            </td>
            <td width="350" style="font-size: 12px; color: #888; font-weight: normal;">Model used to categorize uploaded docs (type, audience, topics). Default gpt-4o-mini is cheap and reliable.</td>
          </tr>
          <tr>
            <td align="right" width="400">OpenAI API Key</td>
            <td width="500">
              <b-form-input v-model="model.openai_api_key" type="password" placeholder="Leave blank to use default"></b-form-input>
            </td>
            <td width="350" style="font-size: 12px; color: #888; font-weight: normal;">Tenant-specific override for billing</td>
          </tr>

          <!-- Appearance Settings -->
          <tr>
            <td colspan="3" style="padding-top: 30px;">
              <span style="color: steelblue; font-weight: bold; font-size: 20px;">Appearance</span>
              <hr style="margin: 5px 0 15px 0;"/>
            </td>
          </tr>
          <tr>
            <td align="right" width="400">Thinking Animation</td>
            <td width="500">
              <b-form-select v-model="model.fleet_assistant_animation" :options="animation_options"></b-form-select>
            </td>
            <td width="350" style="font-size: 12px; color: #888; font-weight: normal;">Animation shown while AI is thinking</td>
          </tr>
          <tr>
            <td align="right" width="400">Preview</td>
            <td width="500">
              <div class="animation-preview">
                <div class="thinking-indicator">
                  <component :is="currentAnimationComponent" />
                </div>
              </div>
            </td>
            <td width="350">&nbsp;</td>
          </tr>

          <!-- Behavior Settings -->
          <tr>
            <td colspan="3" style="padding-top: 30px;">
              <span style="color: steelblue; font-weight: bold; font-size: 20px;">Behavior</span>
              <hr style="margin: 5px 0 15px 0;"/>
            </td>
          </tr>
          <tr>
            <td align="right" valign="top" width="400">Custom Instructions</td>
            <td width="500">
              <b-form-textarea
                v-model="model.fleet_assistant_instructions"
                placeholder="Add tenant-specific instructions for the AI assistant...

Examples:
- Always mention our company name 'ABC Transport' in responses
- Drivers should be referred to as 'operators'
- When discussing bookings, always include the customer name
- Prioritize fuel efficiency in routing suggestions"
                style="field-sizing: content; font-size: 14px; min-height: 180px;"
              ></b-form-textarea>
              <div style="font-size: 11px; color: #888; margin-top: 4px;">
                {{ (model.fleet_assistant_instructions || '').length }} / 2000 characters
              </div>
            </td>
            <td width="350" valign="top" style="font-size: 12px; color: #888; font-weight: normal;">
              Additional instructions for the Fleet Assistant to follow. Use this to customize terminology, add company-specific rules, or guide how the assistant responds.
            </td>
          </tr>

          <!-- Company documents (admin) -->
          <tr>
            <td colspan="3" style="padding-top: 30px;">
              <span style="color: steelblue; font-weight: bold; font-size: 20px;">Documents</span>
              <hr style="margin: 5px 0 15px 0;"/>
            </td>
          </tr>
          <tr>
            <td colspan="3">
              <p style="font-size: 13px; color: #666; margin-bottom: 12px; font-weight: normal;">
                Add company documents for Fleet Assistant to reference, such as a company handbook.  The assistant uses these when answering questions.</br>
                Uploads are limited to PDF, DOCX, or TXT files (max 25 documents, 25 MB each).
              </p>

              <div v-if="docStoresLoading" class="text-muted small">Loading document stores…</div>
              <table v-else class="doc-stores-table">
                <thead>
                  <tr>
                    <th>Owner</th>
                    <th style="text-align: right;"># Docs</th>
                    <th style="text-align: right;">Total Size</th>
                    <th style="width: 120px;"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="store in docStoresForDisplay" :key="store.owner" :class="{ 'store-company': store.owner === 'company' }">
                    <td class="store-owner">
                      <span v-if="store.owner === 'company'" class="user-label company-label">General Documents</span>
                      <span v-else class="user-label">{{ store.owner }}</span>
                    </td>
                    <td class="store-count">
                      <span
                        v-if="store.processing_count"
                        class="doc-badge processing pulsing store-processing"
                      >
                        LEARNING {{ store.processing_count }}
                      </span>
                      {{ store.doc_count }}
                    </td>
                    <td class="store-size">{{ formatFileSize(store.total_size) }}</td>
                    <td class="store-actions">
                      <b-button
                        v-if="store.owner === 'company'"
                        size="sm"
                        variant="outline-primary"
                        @click="openCompanyDocModal"
                      >
                        Manage
                      </b-button>
                      <b-button
                        v-else
                        size="sm"
                        variant="outline-danger"
                        @click="purge_user_docs(store.owner)"
                        :disabled="purgingUser === store.owner"
                      >
                        <span v-if="purgingUser === store.owner"><b-spinner small></b-spinner></span>
                        <span v-else>Purge</span>
                      </b-button>
                    </td>
                  </tr>
                </tbody>
              </table>

              <!-- Company Documents Modal -->
              <b-modal
                v-model="showCompanyDocModal"
                title="Manage Company Documents"
                size="lg"
                hide-footer
                @hidden="onCompanyDocModalClosed"
              >
                <p style="font-size: 13px; color: #666; margin-bottom: 12px;">
                  Add company documents for Fleet Assistant to reference, such as the employee handbook. The assistant uses these when answering questions. Upload PDF, DOCX, or TXT files. Max 25 MB per file. Max 25 documents.
                </p>

                <!-- Upload row -->
                <div class="d-flex align-items-center gap-2 mb-3">
                  <input
                    ref="companyDocPicker"
                    type="file"
                    accept=".pdf,.docx,.txt"
                    style="display: none"
                    multiple
                    @change="onCompanyDocPicked"
                  />
                  <b-button size="sm" variant="primary" @click="$refs.companyDocPicker.click()">
                    Add Document(s)
                  </b-button>
                  <b-button
                    v-if="selectedDocs.length > 0"
                    class="ml-2"
                    size="sm"
                    variant="danger"
                    @click="delete_selected_docs"
                    :disabled="deletingSelected"
                  >
                    <span v-if="deletingSelected"><b-spinner small></b-spinner> DELETING {{ deleteProgress }}</span>
                    <span v-else>Delete Selected ({{ selectedDocs.length }})</span>
                  </b-button>
                  <span v-if="companyDocsError" class="text-danger small">{{ companyDocsError }}</span>
                </div>

                <!-- Summary -->
                <div v-if="companyDocs.length > 0" class="docs-summary mb-2">
                  <strong>{{ companyDocs.length }}</strong> document{{ companyDocs.length === 1 ? '' : 's' }} · <strong>{{ formatFileSize(companyDocsTotalSize) }}</strong> total
                </div>

                <!-- Documents table -->
                <div v-if="companyDocsLoading" class="text-muted small">Loading documents…</div>
                <div v-else-if="companyDocs.length === 0" class="text-muted small">No company documents uploaded yet.</div>
                <table v-else class="company-docs-table">
                  <thead>
                    <tr>
                      <th style="width: 40px;">
                        <input
                          type="checkbox"
                          :checked="allDocsSelected"
                          :indeterminate.prop="someDocsSelected && !allDocsSelected"
                          @change="toggleSelectAll"
                        />
                      </th>
                      <th>Name</th>
                      <th>Uploaded By</th>
                      <th>Date</th>
                      <th>Size</th>
                      <th style="width: 80px;"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="doc in companyDocs"
                      :key="doc.doc_id || doc.temp_id"
                      :class="{ 'row-selected': doc.doc_id && selectedDocs.includes(doc.doc_id), 'row-uploading': doc.status === 'uploading' || doc.status === 'processing', 'row-failed': doc.status === 'failed' || doc.status === 'delete-failed', 'row-deleting': doc.status === 'deleting' }"
                    >
                      <td>
                        <input
                          type="checkbox"
                          :checked="doc.doc_id && selectedDocs.includes(doc.doc_id)"
                          :disabled="!doc.doc_id || doc.status === 'uploading' || doc.status === 'processing' || doc.status === 'deleting'"
                          @change="toggleDocSelection(doc.doc_id)"
                        />
                      </td>
                      <td class="doc-name" :title="doc.display_name">{{ doc.display_name }}</td>
                      <td class="doc-uploader">{{ doc.uploaded_by || '—' }}</td>
                      <td class="doc-date">
                        <span v-if="doc.status === 'uploading'" class="doc-badge upload">UPLOADING</span>
                        <span v-else-if="doc.status === 'processing'" class="doc-badge processing pulsing">LEARNING</span>
                        <span v-else-if="doc.status === 'deleting'" class="doc-badge deleting pulsing">DELETING</span>
                        <span v-else-if="doc.status === 'delete-failed'" class="doc-badge failed">DELETE FAILED</span>
                        <span v-else-if="doc.status === 'failed'" class="doc-badge failed">FAILED</span>
                        <span v-else>{{ formatDocDate(doc.uploaded_at) }}</span>
                      </td>
                      <td class="doc-size">{{ formatFileSize(doc.file_size) }}</td>
                      <td class="doc-actions">
                        <b-button
                          size="sm"
                          variant="outline-danger"
                          @click="delete_company_doc(doc.doc_id)"
                          :disabled="!doc.doc_id || doc.status === 'uploading' || doc.status === 'processing' || doc.status === 'deleting' || docDeleting === doc.doc_id || deletingSelected"
                        >
                          <span v-if="docDeleting === doc.doc_id"><b-spinner small></b-spinner></span>
                          <span v-else>Delete</span>
                        </b-button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </b-modal>
            </td>
          </tr>

        </table>

        <hr/>
        <div style="position: relative" class="d-flex align-items-center justify-content-center">
          <b-btn size="lg" type="submit" variant="success" id="save-user-submit">Save</b-btn>
          &nbsp;&nbsp;<b-btn size="lg" @click="load_data()">Cancel</b-btn>
        </div>

      </form>
    </div>
    <div v-else>
      <br/><br/>
      <b-spinner style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
    </div>
  </div>

</template>

<script>
import { get_tenant_config_settings, update_tenant_config_settings, get_chat_instructions, list_fa_documents_company, presign_fa_document_company, finalize_fa_document_company, delete_fa_document_company, get_fa_documents_summary, purge_fa_documents_user, get_ai_usage, reset_ai_usage, get_ai_usage_history } from '../networking/apis'
import { tbn_get_chat_instructions } from '../networking/tbn_apis'
import {mapState} from "vuex";
import {
  DotsAnimation,
  SpinnerAnimation,
  VanAnimation,
  CoachBusAnimation,
  SchoolBusAnimation,
  ShuttleAnimation,
  LimoAnimation,
  TaxiAnimation,
  animationComponents
} from './animations'

export default {
  name: 'tenant_config_fleet_assistant',
  components: {
    DotsAnimation,
    SpinnerAnimation,
    VanAnimation,
    CoachBusAnimation,
    SchoolBusAnimation,
    ShuttleAnimation,
    LimoAnimation,
    TaxiAnimation
  },
  data() {
    return {
      group_name: 'general',
      model: {
        openai_model: 'gpt-5.2',
        openai_doc_model: 'gpt-4o-mini',
        openai_api_key: '',
        fleet_assistant_animation: 'dots',
        fleet_assistant_instructions: '',
        ai_cost_limit_cents: 2000,
      },
      usagePercent: 0,
      usagePeriod: '',
      usageOverLimit: false,
      usageChatPercent: 0,
      usageDocsPercent: 0,
      usageLimitDollars: '20',
      resettingUsage: false,
      defaultInstructions: 'Loading instructions...',
      loading: true,
      openai_model_options: [
        { text: 'GPT-4o mini - fast, affordable', value: 'gpt-4o-mini' },
        { text: 'GPT-4o - smarter, handles complex queries', value: 'gpt-4o' },
        { text: 'GPT-4.1 mini - newer, faster alternative', value: 'gpt-4.1-mini' },
        { text: 'GPT-4.1 - latest smart non-reasoning model', value: 'gpt-4.1' },
        { text: 'GPT-5 nano - fastest GPT-5 (may miss complex patterns)', value: 'gpt-5-nano' },
        { text: 'GPT-5 mini - best balance of speed and intelligence', value: 'gpt-5-mini' },
        { text: 'GPT-5.2 - most capable (default)', value: 'gpt-5.2' },
      ],
      openai_doc_model_options: [
        { text: 'GPT-4o mini - cheap, reliable (default)', value: 'gpt-4o-mini' },
        { text: 'GPT-4o', value: 'gpt-4o' },
        { text: 'GPT-4.1 mini', value: 'gpt-4.1-mini' },
        { text: 'GPT-4.1', value: 'gpt-4.1' },
        { text: 'GPT-5 mini', value: 'gpt-5-mini' },
        { text: 'GPT-5.2', value: 'gpt-5.2' },
      ],
      animation_options: [
        { text: 'Typing Dots', value: 'dots' },
        { text: 'Van', value: 'van' },
        { text: 'Coach Bus', value: 'coach' },
        { text: 'School Bus', value: 'school' },
        { text: 'Shuttle', value: 'shuttle' },
        { text: 'Limousine', value: 'limo' },
        { text: 'Taxi', value: 'taxi' },
        { text: 'Thinking...', value: 'spinner' },
      ],
      companyDocs: [],
      companyDocsLoading: false,
      companyDocsError: '',
      companyUploading: false,
      docDeleting: null,
      // Document stores
      docStores: [],
      docStoresLoading: false,
      showCompanyDocModal: false,
      purgingUser: null,
      // Multi-select for company docs
      selectedDocs: [],
      deletingSelected: false,
      deleteProgress: '',
      _companyDocsPollTimer: null,
      _companyDocsPollDelay: 2000,
      _docStoresPollTimer: null,
      _docStoresPollDelay: 2000,
      // usage details expander (persists across tenant switches)
      usageDetailsExpanded: false,
      usageHistory: [],
      usageHistoryLoading: false,
    }
  },
  async mounted() {
    this.load_data()
  },
  beforeDestroy() {
    this.stopCompanyDocsRefresh()
    this.stopDocStoresRefresh()
  },
  watch: {
    'user.tenant'() {
      this.load_data()
    }
  },
  methods: {
    normalizeCostLimitCents(value) {
      const parsed = parseInt(value, 10)
      if (Number.isNaN(parsed) || parsed <= 0) return 2000
      return parsed
    },
    normalizeUsagePercent(value) {
      const parsed = parseInt(value, 10)
      if (Number.isNaN(parsed) || parsed < 0) return 0
      if (parsed > 100) return 100
      return parsed
    },
    normalizeUsageSplitPercent(value) {
      const parsed = parseInt(value, 10)
      if (Number.isNaN(parsed) || parsed < 0) return 0
      if (parsed > 100) return 100
      return parsed
    },
    async load_usage_history() {
      this.usageHistoryLoading = true
      try {
        const { value: { data = {} } } = await get_ai_usage_history(this.user.tenant, 12)
        const result = data.data || data
        const now = new Date()
        const currentPeriod = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`
        this.usageHistory = (result.months || []).map(row => ({
          ...row,
          is_current: row.period === currentPeriod,
        }))
      } catch (e) {
        console.warn('Failed to load usage history:', e)
        this.usageHistory = []
      } finally {
        this.usageHistoryLoading = false
      }
    },
    formatHistoryPeriod(period) {
      if (!period || !/^\d{4}-\d{2}$/.test(period)) return period || ''
      const [year, month] = period.split('-')
      const date = new Date(Number(year), Number(month) - 1, 1)
      if (Number.isNaN(date.getTime())) return period
      return date.toLocaleDateString(undefined, { year: 'numeric', month: 'long' })
    },
    formatTokenCount(count) {
      if (!count || count === 0) return '0'
      if (count >= 1000000) return (count / 1000000).toFixed(1) + 'M'
      if (count >= 1000) return (count / 1000).toFixed(1) + 'K'
      return count.toLocaleString()
    },
    async load_usage_summary() {
      try {
        const { value: { data = {} } } = await get_ai_usage(this.user.tenant)
        const usage = data.data || data
        this.usagePercent = this.normalizeUsagePercent(usage.percentage)
        this.usagePeriod = usage.period || ''
        this.usageOverLimit = !!usage.over_limit
        this.usageChatPercent = this.normalizeUsageSplitPercent(usage.chat_percentage)
        this.usageDocsPercent = this.normalizeUsageSplitPercent(usage.docs_percentage)
      } catch (e) {
        this.usagePercent = 0
        this.usagePeriod = ''
        this.usageOverLimit = false
        this.usageChatPercent = 0
        this.usageDocsPercent = 0
      }
    },
    async reset_usage_stats() {
      if (!this.isAdmin) return
      const periodLabel = this.usagePeriodLabel || 'the current month'
      const confirmed = confirm(
        `Reset Fleet Assistant usage stats for ${periodLabel}? This will clear the current month counters for this tenant.`
      )
      if (!confirmed) return

      this.resettingUsage = true
      try {
        const { value: { data = {} } } = await reset_ai_usage(this.user.tenant)
        const result = data.data || data
        await Promise.all([this.load_usage_summary(), this.load_usage_history()])
        const deletedRows = parseInt(result.deleted_rows, 10) || 0
        this.$emit(
          'showAlert',
          `Usage stats reset. Cleared ${deletedRows} record${deletedRows === 1 ? '' : 's'}.`,
          'success',
          5000
        )
      } catch (e) {
        const errorMessage = e && e.response && e.response.data && e.response.data.error
          ? e.response.data.error
          : 'Failed to reset usage stats'
        this.$emit('showAlert', errorMessage, 'danger', 5000)
      } finally {
        this.resettingUsage = false
      }
    },
    async load_data() {
      this.loading = true
      try {
        const {value: { data = [] } } = await get_tenant_config_settings(this.user.tenant)
        const results = data.results || {}
        
        // merge loaded data with defaults (preserves reactivity)
        this.model.openai_model = results.openai_model || 'gpt-5.2'
        this.model.openai_doc_model = results.openai_doc_model || 'gpt-4o-mini'
        this.model.openai_api_key = results.openai_api_key || ''
        this.model.fleet_assistant_animation = results.fleet_assistant_animation || 'dots'
        this.model.fleet_assistant_instructions = results.fleet_assistant_instructions || ''
        this.model.ai_cost_limit_cents = this.normalizeCostLimitCents(results.ai_cost_limit_cents)
        this.usageLimitDollars = String(Math.round(this.model.ai_cost_limit_cents / 100))

        // fetch default instructions from backend (use TBN or CM endpoint based on tenant)
        try {
          const isTbnTenant = this.user.tenant && this.user.tenant.includes('-')
          const instructionsResponse = isTbnTenant
            ? await tbn_get_chat_instructions(this.user.tenant)
            : await get_chat_instructions(this.user.tenant)
          
          const instructions = instructionsResponse &&
            instructionsResponse.value &&
            instructionsResponse.value.data &&
            instructionsResponse.value.data.instructions
          if (instructions) {
            this.defaultInstructions = instructions
          }
        } catch (instructionsError) {
          console.warn('Failed to load default instructions:', instructionsError)
          this.defaultInstructions = 'Unable to load default instructions'
        }

        await Promise.all([
          this.load_doc_stores(),
          this.load_usage_summary(),
          this.isAdmin ? this.load_usage_history() : Promise.resolve(),
        ])
      } catch(e) {
        this.$emit('showAlert', 'error retrieving data, try logging in again or contact support if needed', 'danger', 5000)
      }

      this.loading = false
    },
    async load_doc_stores(silent = false) {
      if (!silent) {
        this.docStoresLoading = true
      }
      try {
        const { value: { data = {} } } = await get_fa_documents_summary(this.user.tenant)
        const results = data.data || data
        this.docStores = results.stores || []
        this.scheduleDocStoresRefresh()
      } catch (e) {
        console.warn('Failed to load document stores:', e)
        if (!silent) {
          this.docStores = []
        }
      } finally {
        if (!silent) {
          this.docStoresLoading = false
        }
      }
    },
    async load_company_docs(silent = false) {
      if (!silent) {
        this.companyDocsError = ''
        this.companyDocsLoading = true
      }
      try {
        const { value: { data = {} } } = await list_fa_documents_company(this.user.tenant)
        const results = data.data || data
        const serverDocs = results.documents || []

        const existing = this.companyDocs || []
        const existingByDocId = new Map()
        for (const doc of existing) {
          if (doc && doc.doc_id) existingByDocId.set(doc.doc_id, doc)
        }

        const merged = []
        for (const doc of serverDocs) {
          const current = existingByDocId.get(doc.doc_id)
          if (current) {
            Object.assign(current, doc)
            merged.push(current)
          } else {
            merged.push({ ...doc })
          }
        }

        const serverDocIds = new Set(serverDocs.map(d => d.doc_id).filter(Boolean))
        const localInProgress = existing.filter(d =>
          (d.status === 'uploading' || d.status === 'processing' || d.status === 'failed') &&
          (!d.doc_id || !serverDocIds.has(d.doc_id))
        )

        const combined = [...merged, ...localInProgress]
        this.companyDocs = combined.sort((a, b) => (b.uploaded_at || 0) - (a.uploaded_at || 0))
        const validIds = new Set(this.companyDocs.map(d => d.doc_id).filter(Boolean))
        this.selectedDocs = this.selectedDocs.filter(id => validIds.has(id))
        this.scheduleCompanyDocsRefresh()
      } catch (e) {
        console.warn('Failed to load company documents:', e)
        if (!silent) {
          this.companyDocsError = e.response && e.response.data && e.response.data.error ? e.response.data.error : 'Could not load company documents'
          this.companyDocs = (this.companyDocs || []).filter(d =>
            d.status === 'uploading' || d.status === 'processing' || d.status === 'failed'
          )
        }
      } finally {
        if (!silent) {
          this.companyDocsLoading = false
        }
      }
    },
    onCompanyDocPicked(event) {
      const files = event.target.files
      if (files && files.length > 0) {
        this.upload_company_docs(Array.from(files))
      }
      // Reset picker so same file can be selected again
      if (this.$refs.companyDocPicker) {
        this.$refs.companyDocPicker.value = ''
      }
    },
    async upload_company_docs(files) {
      if (!files || files.length === 0) return
      this.companyUploading = true
      this.companyDocsError = ''
      
      let successCount = 0
      let failCount = 0
      const MAX_FILE_SIZE = 25 * 1024 * 1024 // 25 MB
      const MAX_DOCS = 25
      const currentCount = (this.companyDocs || []).filter(d =>
        d.doc_id || d.status === 'uploading' || d.status === 'processing'
      ).length
      const CONCURRENCY = 3
      
      // validate files and create placeholders upfront
      const items = []
      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        if (file.size > MAX_FILE_SIZE) {
          const sizeMB = (file.size / (1024 * 1024)).toFixed(1)
          console.warn(`File too large: ${file.name}`)
          failCount++
          this.companyDocsError = `${file.name} is ${sizeMB} MB — maximum is 25 MB`
          continue
        }
        if (currentCount + items.length >= MAX_DOCS) {
          console.warn('Company document limit reached')
          failCount++
          this.companyDocsError = `Document limit reached (${MAX_DOCS}). Delete a document to add another.`
          break
        }
        const tempId = `temp-${Date.now()}-${i}-${Math.random().toString(16).slice(2)}`
        const placeholder = {
          temp_id: tempId,
          doc_id: null,
          display_name: file.name,
          uploaded_by: (this.user && (this.user.email || this.user.username)) || '—',
          uploaded_at: Math.floor(Date.now() / 1000),
          file_size: file.size,
          status: 'uploading',
        }
        this.companyDocs = [placeholder, ...(this.companyDocs || [])]
        items.push({ file, tempId })
      }
      
      // worker pool: 3 workers drain the queue, finalize fires in background
      const finalizePromises = []
      let index = 0

      const worker = async () => {
        while (index < items.length) {
          const { file, tempId } = items[index++]
          const ok = await this._uploadCompanyItem(file, tempId, finalizePromises)
          if (ok) successCount++
          else failCount++
        }
      }

      await Promise.all(
        Array.from({ length: Math.min(CONCURRENCY, items.length) }, () => worker())
      )
      // wait for trailing finalize calls to settle
      const finalizeResults = await Promise.allSettled(finalizePromises)
      for (const r of finalizeResults) {
        if (r.status === 'rejected' || (r.status === 'fulfilled' && !r.value)) {
          // upload succeeded but finalize failed — already counted as success above,
          // bump fail and decrement success to correct the counts
          failCount++
          successCount = Math.max(0, successCount - 1)
        }
      }
      
      await this.load_company_docs()
      this.companyUploading = false
      
      if (failCount === 0) {
        this.$root.$emit('fa-docs-changed')
        this.$emit('showAlert', `Uploaded ${successCount} document(s)`, 'success', 3000)
      } else if (successCount === 0) {
        this.companyDocsError = 'Upload failed'
        this.$emit('showAlert', 'Upload failed', 'danger', 5000)
      } else {
        this.$root.$emit('fa-docs-changed')
        this.$emit('showAlert', `Uploaded ${successCount}, failed ${failCount}`, 'warning', 5000)
      }
    },
    // presign + S3 upload, then kick off finalize in the background
    async _uploadCompanyItem(file, tempId, finalizePromises) {
      try {
        const filename = file && file.name ? file.name : 'document'
        const contentType = file && file.type ? file.type : 'application/octet-stream'
        const presignResp = await presign_fa_document_company(this.user.tenant, {
          filename,
          content_type: contentType,
          file_size: file && file.size ? file.size : 0,
          display_name: filename,
        })
        const presignData = presignResp && presignResp.value && presignResp.value.data ? (presignResp.value.data.data || presignResp.value.data) : {}
        const upload = presignData.upload || {}
        const uploadForm = new FormData()
        Object.entries(upload.fields || {}).forEach(([k, v]) => uploadForm.append(k, v))
        uploadForm.append('file', file)
        const s3Resp = await fetch(upload.url, { method: 'POST', body: uploadForm })
        if (!s3Resp.ok) {
          throw new Error('S3 upload failed')
        }
        // S3 upload done — mark as processing and fire off finalize in background
        this.companyDocs = (this.companyDocs || []).map(d => d.temp_id === tempId ? { ...d, status: 'processing', doc_id: presignData.doc_id } : d)
        finalizePromises.push(this._finalizeCompanyItem(tempId, presignData, filename, contentType))
        return true
      } catch (e) {
        console.warn(`Failed to upload ${file.name}:`, e)
        this.companyDocs = (this.companyDocs || []).map(d => d.temp_id === tempId ? { ...d, status: 'failed' } : d)
        return false
      }
    },
    async _finalizeCompanyItem(tempId, presignData, filename, contentType) {
      try {
        const finalizeResp = await finalize_fa_document_company(this.user.tenant, {
          doc_id: presignData.doc_id,
          filename,
          content_type: contentType,
          display_name: presignData.display_name || filename,
        })
        const finalized = finalizeResp && finalizeResp.value && finalizeResp.value.data ? (finalizeResp.value.data.data || finalizeResp.value.data) : {}
        this.companyDocs = (this.companyDocs || []).map(d => {
          if (d.temp_id !== tempId) return d
          return {
            ...d,
            doc_id: finalized.doc_id || presignData.doc_id || d.doc_id,
            display_name: finalized.display_name || presignData.display_name || d.display_name,
            uploaded_at: finalized.uploaded_at || d.uploaded_at,
            uploaded_by: finalized.uploaded_by || d.uploaded_by,
            file_size: finalized.file_size || d.file_size,
            status: undefined,
          }
        })
        return true
      } catch (e) {
        console.warn(`Failed to finalize ${filename}:`, e)
        this.companyDocs = (this.companyDocs || []).map(d => d.temp_id === tempId ? { ...d, status: 'failed' } : d)
        return false
      }
    },
    formatDocDate(timestamp) {
      if (!timestamp) return '—'
      const d = new Date(timestamp * 1000)
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    },
    formatFileSize(bytes) {
      if (!bytes || bytes === 0) return '—'
      if (bytes < 1024) return bytes + ' B'
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
    },
    async delete_company_doc(docId) {
      if (!confirm('Delete this document? The assistant will no longer use it.')) return
      this.docDeleting = docId
      try {
        await delete_fa_document_company(this.user.tenant, docId)
        await this.load_company_docs()
        this.$root.$emit('fa-docs-changed')
        this.$emit('showAlert', 'Document deleted', 'success', 3000)
      } catch (e) {
        this.$emit('showAlert', e.response && e.response.data && e.response.data.error ? e.response.data.error : 'Delete failed', 'danger', 5000)
      } finally {
        this.docDeleting = null
      }
    },
    scheduleCompanyDocsRefresh() {
      if (!this.showCompanyDocModal) {
        this.stopCompanyDocsRefresh()
        return
      }
      const hasProcessing = (this.companyDocs || []).some(d => (d.status || 'uploaded') === 'processing')
      if (!hasProcessing) {
        this.stopCompanyDocsRefresh()
        return
      }
      if (this._companyDocsPollTimer) return
      const delay = this._companyDocsPollDelay || 2000
      this._companyDocsPollTimer = setTimeout(async () => {
        this._companyDocsPollTimer = null
        if (this.companyDocsLoading) {
          this.scheduleCompanyDocsRefresh()
          return
        }
        await this.load_company_docs(true)
        this._companyDocsPollDelay = Math.min(Math.round((this._companyDocsPollDelay || 2000) * 1.5), 15000)
      }, delay)
    },
    stopCompanyDocsRefresh() {
      if (this._companyDocsPollTimer) {
        clearTimeout(this._companyDocsPollTimer)
        this._companyDocsPollTimer = null
      }
      this._companyDocsPollDelay = 2000
    },
    scheduleDocStoresRefresh() {
      const serverProcessing = (this.docStores || []).some(s => (s.processing_count || 0) > 0)
      const localInFlight = (this.companyDocs || []).some(d => d.status === 'uploading' || d.status === 'processing')
      if (!serverProcessing && !localInFlight) {
        this.stopDocStoresRefresh()
        return
      }
      if (this._docStoresPollTimer) return
      const delay = this._docStoresPollDelay || 2000
      this._docStoresPollTimer = setTimeout(async () => {
        this._docStoresPollTimer = null
        if (this.docStoresLoading) {
          this.scheduleDocStoresRefresh()
          return
        }
        await this.load_doc_stores(true)
        this._docStoresPollDelay = Math.min(Math.round((this._docStoresPollDelay || 2000) * 1.5), 15000)
      }, delay)
    },
    stopDocStoresRefresh() {
      if (this._docStoresPollTimer) {
        clearTimeout(this._docStoresPollTimer)
        this._docStoresPollTimer = null
      }
      this._docStoresPollDelay = 2000
    },
    async openCompanyDocModal() {
      this.showCompanyDocModal = true
      await this.load_company_docs()
    },
    onCompanyDocModalClosed() {
      this.selectedDocs = []
      this.stopCompanyDocsRefresh()
      this.load_doc_stores()
    },
    async purge_user_docs(userId) {
      const store = this.docStores.find(s => s.owner === userId)
      const count = store ? store.doc_count : 0
      if (!confirm(`Delete ALL ${count} document(s) for user "${userId}"? This cannot be undone.`)) return
      this.purgingUser = userId
      try {
        const { value: { data = {} } } = await purge_fa_documents_user(this.user.tenant, userId)
        const results = data.data || data
        const deletedCount = results.deleted_count || 0
        this.$root.$emit('fa-docs-changed')
        this.$emit('showAlert', `Purged ${deletedCount} document(s) for ${userId}`, 'success', 3000)
        await this.load_doc_stores()
      } catch (e) {
        this.$emit('showAlert', e.response && e.response.data && e.response.data.error ? e.response.data.error : 'Purge failed', 'danger', 5000)
      } finally {
        this.purgingUser = null
      }
    },
    toggleDocSelection(docId) {
      if (!docId) return
      const idx = this.selectedDocs.indexOf(docId)
      if (idx === -1) {
        this.selectedDocs.push(docId)
      } else {
        this.selectedDocs.splice(idx, 1)
      }
    },
    toggleSelectAll() {
      if (this.allDocsSelected) {
        this.selectedDocs = []
      } else {
        this.selectedDocs = this.selectableCompanyDocs.map(d => d.doc_id)
      }
    },
    async delete_selected_docs() {
      const toDelete = [...this.selectedDocs]
      const count = toDelete.length
      if (count === 0) return
      if (!confirm(`Delete ${count} selected document(s)? This cannot be undone.`)) return
      
      this.deletingSelected = true
      let completed = 0
      let successCount = 0
      let failCount = 0
      this.deleteProgress = `0/${count}`
      
      // mark all selected rows as deleting upfront
      const deleteSet = new Set(toDelete)
      this.companyDocs = this.companyDocs.map(d => deleteSet.has(d.doc_id) ? { ...d, status: 'deleting' } : d)
      
      const CONCURRENCY = 3
      let index = 0
      
      const worker = async () => {
        while (index < toDelete.length) {
          const docId = toDelete[index++]
          try {
            await delete_fa_document_company(this.user.tenant, docId)
            successCount++
            // remove from list immediately on success
            this.companyDocs = (this.companyDocs || []).filter(d => d.doc_id !== docId)
          } catch (e) {
            console.warn(`Failed to delete doc ${docId}:`, e)
            failCount++
            this.companyDocs = (this.companyDocs || []).map(d => d.doc_id === docId ? { ...d, status: 'delete-failed' } : d)
          }
          completed++
          this.deleteProgress = `${completed}/${count}`
        }
      }
      
      await Promise.all(
        Array.from({ length: Math.min(CONCURRENCY, toDelete.length) }, () => worker())
      )
      
      this.selectedDocs = []
      await this.load_company_docs()
      this.deletingSelected = false
      this.deleteProgress = ''
      
      if (failCount === 0) {
        this.$emit('showAlert', `Deleted ${successCount} document(s)`, 'success', 3000)
      } else {
        this.$emit('showAlert', `Deleted ${successCount}, failed ${failCount}`, 'warning', 5000)
      }
    },
    async save_config() {
      // validate custom instructions length
      if (this.model.fleet_assistant_instructions && this.model.fleet_assistant_instructions.length > 2000) {
        this.$emit('showAlert', 'Custom instructions must be 2000 characters or less', 'warning', 5000)
        return
      }

      if (this.canEditCostLimit) {
        const dollars = parseInt(this.usageLimitDollars, 10)
        if (Number.isNaN(dollars) || dollars <= 0) {
          this.$emit('showAlert', 'Monthly Cost Limit must be a positive whole dollar amount', 'warning', 5000)
          return
        }
        this.model.ai_cost_limit_cents = dollars * 100
      }
      
      try {
        window.scrollTo(0,0);
        this.$emit('showAlert', 'Saving Changes', 'info', 5000)
        await update_tenant_config_settings(this.user.tenant, this.model, this.group_name)
        this.load_data()
        this.$root.$emit('fa-settings-changed')
        this.$emit('showAlert', 'Configuration Saved', 'success', 5000)
      } catch(e) {
        this.$emit('showAlert', 'error updating data, try logging in again or contact support if needed', 'danger', 5000)
      }
    },
  },
  computed: {
    ...mapState({
      user: state => state.profile.user
    }),
    isAdmin() {
      return (this.user && this.user.role) === 'admin'
    },
    canEditCostLimit() {
      const role = this.user && this.user.role
      return role === 'admin' || role === 'customer-admin'
    },
    docStoresForDisplay() {
      if (this.docStores && this.docStores.length > 0) {
        return this.docStores
      }
      return [{ owner: 'company', doc_count: 0, total_size: 0, processing_count: 0 }]
    },
    usageSplitNormalized() {
      if (this.usagePercent <= 0) {
        return { chat: 0, docs: 0 }
      }
      const chat = this.normalizeUsageSplitPercent(this.usageChatPercent)
      const docs = this.normalizeUsageSplitPercent(this.usageDocsPercent)
      const total = chat + docs
      if (total <= 0) {
        // Fallback for legacy rows where split data is unavailable.
        return { chat: 100, docs: 0 }
      }
      return {
        chat: (chat / total) * 100,
        docs: (docs / total) * 100,
      }
    },
    usageChatBarValue() {
      return (this.usagePercent * this.usageSplitNormalized.chat) / 100
    },
    usageDocsBarValue() {
      return (this.usagePercent * this.usageSplitNormalized.docs) / 100
    },
    usageChatMonthPercent() {
      return this.normalizeUsagePercent(Math.round(this.usageChatBarValue))
    },
    usageDocsMonthPercent() {
      return this.normalizeUsagePercent(Math.max(0, this.usagePercent - this.usageChatMonthPercent))
    },
    usageFreePercent() {
      return Math.max(0, 100 - this.usagePercent)
    },
    usageRemainingDisplay() {
      return this.normalizeUsagePercent(Math.round(this.usageFreePercent))
    },
    usageNearLimit() {
      return this.usagePercent >= 80
    },
    usageBarVariant() {
      return this.usageNearLimit ? 'warning' : 'primary'
    },
    usageRemainingBarValue() {
      return this.usageFreePercent
    },
    usagePeriodLabel() {
      if (!this.usagePeriod || !/^\d{4}-\d{2}$/.test(this.usagePeriod)) return ''
      const [year, month] = this.usagePeriod.split('-')
      const date = new Date(Number(year), Number(month) - 1, 1)
      if (Number.isNaN(date.getTime())) return ''
      return date.toLocaleDateString(undefined, { year: 'numeric', month: 'long' })
    },
    currentAnimationComponent() {
      return animationComponents[this.model.fleet_assistant_animation] || 'DotsAnimation'
    },
    allDocsSelected() {
      return this.selectableCompanyDocs.length > 0 && this.selectedDocs.length === this.selectableCompanyDocs.length
    },
    someDocsSelected() {
      return this.selectedDocs.length > 0
    },
    selectableCompanyDocs() {
      return (this.companyDocs || []).filter(d =>
        d.doc_id &&
        d.status !== 'uploading' &&
        d.status !== 'processing' &&
        d.status !== 'deleting'
      )
    },
    companyDocsTotalSize() {
      return this.companyDocs.reduce((sum, doc) => sum + (doc.file_size || 0), 0)
    },
    usageHistoryTotalCostDollars() {
      const cents = this.usageHistory.reduce((sum, r) => sum + (r.blended_cost_cents || 0), 0)
      return `$${(cents / 100).toFixed(2)}`
    },
    usageHistoryTotalChatRequests() {
      return this.usageHistory.reduce((sum, r) => sum + (r.chat_requests || 0), 0)
    },
    usageHistoryTotalDocCategorizations() {
      return this.usageHistory.reduce((sum, r) => sum + (r.doc_categorizations || 0), 0)
    },
    usageHistoryTotalInputTokens() {
      return this.usageHistory.reduce((sum, r) => sum + (r.input_tokens || 0), 0)
    },
    usageHistoryTotalOutputTokens() {
      return this.usageHistory.reduce((sum, r) => sum + (r.output_tokens || 0), 0)
    }
  }
}
</script>

<style scoped>
.animation-preview {
  background-color: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 15px 20px;
  display: inline-block;
}

.usage-quota-wrap {
  width: 100%;
}

.usage-progress {
  width: 100%;
  border: 1px solid #d9d9d9;
}

.usage-meta {
  margin-top: 6px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 12px;
  color: #666;
}

.usage-period {
  color: #777;
}

.usage-breakdown-meta {
  justify-content: flex-start;
  gap: 16px;
}

.usage-period-right {
  margin-left: auto;
  text-align: right;
}

.usage-breakdown-item {
  font-weight: 600;
  color: #4b5563;
}

.usage-breakdown-item-chat {
  color: #218838;
}

.usage-breakdown-item-docs {
  color: #1f5fbf;
}

.usage-quota-used,
.usage-quota-remaining {
  font-weight: 600;
  color: #4b5563;
}

.usage-near-limit-cta {
  margin-top: 8px;
  font-size: 13px;
  color: #92400e;
}

.usage-near-limit-cta a {
  font-weight: 600;
  color: #b45309;
}

.usage-near-limit-cta a:hover {
  text-decoration: underline;
}

.usage-remaining-bar {
  background-color: #e9ecef !important;
}

.row-uploading {
  opacity: 0.7;
}

.row-failed {
  color: #b00020;
}

.row-deleting {
  opacity: 0.6;
  background-color: #fff5f5;
}

/* document status badges (mirroring FA chat badges with rounded-square edges) */
.doc-badge {
  --badge-color: #6c757d;
  --badge-bg: #e9ecef;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 8px;
  min-height: 20px;
  font-size: 11px;
  line-height: 14px;
  font-weight: 600;
  border-radius: 4px;
  border: 1px solid var(--badge-color);
  background: var(--badge-bg);
  color: #333;
  white-space: nowrap;
}

.doc-badge.upload {
  --badge-color: #28a745;
  --badge-bg: #e9f6ee;
  color: #155724;
}

.doc-badge.processing {
  --badge-color: #1e7eeb;
  --badge-bg: #e7f1ff;
  --pulse-color: rgba(30, 126, 235, 0.35);
  color: #0b3d91;
}

.store-processing {
  margin-right: 8px;
  font-size: 10px;
}

.doc-badge.deleting {
  --badge-color: #dc3545;
  --badge-bg: #fde8ea;
  --pulse-color: rgba(220, 53, 69, 0.35);
  color: #721c24;
}

.doc-badge.failed {
  --badge-color: #b00020;
  --badge-bg: #fde8ea;
  color: #b00020;
}

.doc-badge.pulsing {
  animation: docBadgePulse 1.2s ease-in-out infinite;
}

@keyframes docBadgePulse {
  0% {
    box-shadow: inset 0 0 0 0 var(--pulse-color);
    opacity: 0.85;
  }
  50% {
    box-shadow: inset 0 0 6px 3px var(--pulse-color);
    opacity: 1;
  }
  100% {
    box-shadow: inset 0 0 0 0 var(--pulse-color);
    opacity: 0.85;
  }
}

.thinking-indicator {
  display: inline-flex;
  align-items: center;
  background-color: #e9ecef;
  color: #333;
  padding: 10px 15px;
  border-radius: 18px;
  gap: 8px;
}


.company-docs-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.company-docs-table th,
.company-docs-table td {
  padding: 8px 10px;
  text-align: left;
  border-bottom: 1px solid #eee;
}

.company-docs-table th {
  font-weight: 600;
  color: #666;
  font-size: 12px;
  text-transform: uppercase;
  background-color: #f8f9fa;
}

.company-docs-table .doc-name {
  max-width: 250px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.company-docs-table .doc-uploader {
  color: #666;
  font-size: 13px;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.company-docs-table .doc-date {
  color: #666;
  font-size: 13px;
  white-space: nowrap;
}

.company-docs-table .doc-size {
  color: #666;
  font-size: 13px;
  white-space: nowrap;
}

.company-docs-table .doc-actions {
  text-align: right;
}

.company-docs-table .row-selected {
  background-color: #fff3cd;
}

.docs-summary {
  font-size: 13px;
  color: #555;
  padding: 6px 10px;
  background-color: #f8f9fa;
  border-radius: 4px;
}

/* Document stores table (list view) */
.doc-stores-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.doc-stores-table th,
.doc-stores-table td {
  padding: 10px 12px;
  border-bottom: 1px solid #eee;
}

.doc-stores-table th {
  font-weight: 600;
  color: #666;
  font-size: 12px;
  text-transform: uppercase;
  background-color: #f8f9fa;
  text-align: left;
}

.doc-stores-table .store-company {
  background-color: #f0f7ff;
}

.doc-stores-table .store-owner {
  font-weight: 500;
}

.doc-stores-table .company-label {
  color: steelblue;
  font-weight: 600;
}

.doc-stores-table .user-label {
  color: #555;
}

.doc-stores-table .company-label {
  font-weight: 700;
}

.doc-stores-table .store-count {
  text-align: right;
  color: #666;
}

.doc-stores-table .store-size {
  text-align: right;
  color: #666;
}

.doc-stores-table .store-actions {
  text-align: right;
}

/* Usage details expander toggle */
.usage-details-toggle {
  display: inline-block;
  margin-top: 8px;
  font-size: 13px;
  font-weight: 600;
  color: steelblue;
  text-decoration: none;
  cursor: pointer;
}

.usage-details-toggle:hover {
  text-decoration: underline;
  color: #3a6d9c;
}

.toggle-chevron {
  display: inline-block;
  font-size: 11px;
  margin-left: 3px;
  transition: transform 0.15s ease;
}

.toggle-chevron-open {
  transform: rotate(180deg);
}

.usage-details-panel {
  margin-top: 10px;
}

/* Usage history table (invoice rollup) */
.usage-history-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  font-weight: normal;
}

.usage-history-table th,
.usage-history-table td {
  padding: 8px 12px;
  border-bottom: 1px solid #eee;
}

.usage-history-table th {
  font-weight: 600;
  color: #666;
  font-size: 12px;
  text-transform: uppercase;
  background-color: #f8f9fa;
  text-align: left;
}

.usage-history-table .history-period {
  font-weight: 500;
  white-space: nowrap;
}

.usage-history-table .history-cost {
  text-align: right;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.usage-history-table .history-count {
  text-align: right;
  color: #555;
  font-variant-numeric: tabular-nums;
}

.usage-history-table .history-tokens {
  text-align: right;
  color: #888;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.usage-history-table .row-current-month {
  background-color: #f0f7ff;
}

.history-current-badge {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 6px;
  font-size: 10px;
  font-weight: 600;
  color: #0b3d91;
  background: #e7f1ff;
  border: 1px solid #1e7eeb;
  border-radius: 4px;
  vertical-align: middle;
}

.usage-history-table tfoot td {
  border-top: 2px solid #ccc;
  border-bottom: none;
  background-color: #f8f9fa;
}

.history-total-row td {
  padding-top: 10px;
}
</style>
